/**
 * RetentionPolicy (AJIP-16.02.004 · WS9)
 * ---------------------------------------------------------------------
 * Per Founder Ruling P-04: retention is configuration-driven. The
 * framework may expose an internal default constant but shall not
 * assume it is the permanent business rule.
 *
 * Construction
 *   new RetentionPolicy({ defaultRetentionMs, overridesByJourneyId })
 *     * `defaultRetentionMs`   consumer-supplied. If omitted, the
 *                              module's DEFAULT_RETENTION_MS is used
 *                              as an internal fallback only.
 *     * `overridesByJourneyId` { [journeyDefinitionId]: retentionMs }
 *
 * Methods
 *   retentionFor(journeyDefinitionId)     → milliseconds
 *   computeExpiresAt(nowMs, journeyId)    → ISO string
 *   isExpired(record, nowMs)              → boolean
 */
const DEFAULT_RETENTION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days — internal fallback ONLY

class RetentionPolicy {
  constructor({ defaultRetentionMs, overridesByJourneyId = {} } = {}) {
    if (defaultRetentionMs !== undefined) {
      if (!Number.isFinite(defaultRetentionMs) || defaultRetentionMs <= 0) {
        throw new Error('defaultRetentionMs must be a positive finite number');
      }
    }
    this._default = defaultRetentionMs === undefined ? DEFAULT_RETENTION_MS : defaultRetentionMs;
    this._overrides = Object.freeze({ ...overridesByJourneyId });
  }

  retentionFor(journeyDefinitionId) {
    if (journeyDefinitionId && this._overrides[journeyDefinitionId] !== undefined) {
      return this._overrides[journeyDefinitionId];
    }
    return this._default;
  }

  computeExpiresAt(nowMs, journeyDefinitionId) {
    const ms = this.retentionFor(journeyDefinitionId);
    return new Date(nowMs + ms).toISOString();
  }

  isExpired(record, nowMs) {
    if (!record || typeof record.expiresAt !== 'string') return true;
    const expMs = Date.parse(record.expiresAt);
    if (Number.isNaN(expMs)) return true;
    return nowMs >= expMs;
  }
}

export { RetentionPolicy, DEFAULT_RETENTION_MS };
